// Currency utilities for display and backend hinting
// - Detection: infer KE users from manual override, locale or timezone
// - Conversion: use env VITE_FX_USD_TO_KES (must match backend FX_USD_TO_KES)

export const detectCountryCode = () => {
  try {
    const manual = (localStorage.getItem('userCountry') || sessionStorage.getItem('userCountry') || '').trim().toUpperCase()
    if (manual && /^[A-Z]{2}$/.test(manual)) return manual
  } catch (_) {}

  try {
    // Prefer navigator.languages first
    const langs = Array.isArray(navigator.languages) ? navigator.languages : [navigator.language].filter(Boolean)
    for (const l of langs) {
      const m = String(l || '').match(/[-_]([A-Za-z]{2})$/)
      if (m && m[1]) {
        const cc = m[1].toUpperCase()
        if (cc === 'KE') return 'KE'
      }
    }
  } catch (_) {}

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    if (String(tz).toLowerCase() === 'africa/nairobi') return 'KE'
  } catch (_) {}

  return ''
}

export const shouldUseKES = () => detectCountryCode() === 'KE'

export const currencySymbol = (code) => {
  const c = String(code || '').toUpperCase()
  if (c === 'KES' || c === 'KSH' || c === 'KSHS') return 'KSH'
  if (c === 'USD') return '$'
  return c || '$'
}

export const getFxUsdToKes = () => {
  const raw = import.meta.env.VITE_FX_USD_TO_KES || '157.0'
  const n = parseFloat(String(raw))
  return isNaN(n) || n <= 0 ? 157.0 : n
}

export const convertUsdToKes = (usd) => {
  const amt = Number(usd || 0)
  const rate = getFxUsdToKes()
  return Math.round(amt * rate * 100) / 100
}

export const formatMoney = (amount, currency) => {
  const amt = Number(amount || 0)
  const cur = String(currency || 'USD').toUpperCase()

  // Explicit KSH rule: uppercase H, single space between code and value
  if (cur === 'KES' || cur === 'KSH' || cur === 'KSHS') {
    return `KSH ${amt.toFixed(2)}`
  }

  // Fallback to Intl for other currencies
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol' }).format(amt)
  } catch (_) {
    const sym = currencySymbol(cur)
    // Use a space only if the symbol is alphabetic (e.g., 'USD', not '$')
    const spacer = /[A-Za-z]/.test(sym) ? ' ' : ''
    return `${sym}${spacer}${amt.toFixed(2)}`
  }
}

export const formatCoursePrice = (priceUsd) => {
  // Input prices from CMS/DRF are stored in USD by design
  if (shouldUseKES()) {
    const kes = convertUsdToKes(priceUsd)
    return { amount: kes, currency: 'KES', text: `KSH ${kes.toFixed(2)}` }
  }
  const usd = Number(priceUsd || 0)
  // Keep standard USD symbol formatting
  return { amount: usd, currency: 'USD', text: `$${usd.toFixed(2)}` }
}

/** Preserve server-quoted prices and regional currency on every course surface. */
export const displayCoursePrice = course => {
  const price = Number(course?.price || 0)
  if (!Number.isFinite(price) || price <= 0) return 'Free'
  return course.pricing ? formatMoney(Number(course.pricing.amount), course.pricing.currency) : formatCoursePrice(price).text
}
